import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import LoadingDots from '../loading-dots';

const buttonVariants = cva(
  'relative cursor-pointer  inline-flex items-center justify-center whitespace-nowrap rounded-[6.25rem] ring-offset-white transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none  font-nunito700 select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 gap-[0.25rem]',
  {
    variants: {
      variant: {
        primary:
          'bg-warp-green-300 duration-0 text-black hover:bg-none hover:bg-warp-green-400 active:bg-warp-green-500 disabled:bg-light-grey-300 disabled:text-dark-grey-100 disabled:font-nunito700',
        secondary:
          'text-black border border-black active:border-warp-green-500 disabled:border-light-grey-400 disabled:text-dark-grey-50 hover:border-warp-green-400 bg-white',
        secondary_black:
          'text-black border border-white active:border-warp-green-500 disabled:border-dark-grey-100 disabled:text-dark-grey-50 hover:border-warp-green-400 bg-black',
        link: 'text-green-500 hover:text-green-700 active:text-green-900 disabled:text-grey-500',
        linkDanger: 'text-red-600 hover:text-red-700 active:text-red-900',
        tertiary:
          'bg-green-500 text-white hover:bg-green-700 active:bg-green-900',
        // TODO: UPDATE hover bg once there is available - new brand updates(blog filters)
        chip: 'text-black border border-black bg-white active:bg-black/0 disabled:text-grey-500 hover:bg-black/10',
        chipSelected: 'bg-black text-warp-green-300 hover:bg-black/80',
        grey: 'bg-deep-teal-50 text-grey-500 hover:bg-deep-teal-100 active:bg-deep-teal-200',
      },
      size: {
        large: 'px-16 py-4 text-xl h-16 [&_svg]:size-6',
        medium: 'px-12 py-spacing-[0.75rem] text-lg h-[3.25rem] [&_svg]:size-6',
        small: 'px-6 py-2 h-10 text-base [&_svg]:size-[1.25rem]',
        extraSmall: 'px-4 py-[0.25rem] h-8 text-base [&_svg]:size-[1.25rem]',
      },
      defaultVariants: {
        variant: 'primary',
        size: 'medium',
      },
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * If true, the button will display a loading indicator and disable click events.
   */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'medium',
      asChild = false,
      loading,
      onClick,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!loading && onClick) {
        onClick(event);
      }
    };
    const isAnimationEnabled =
      (!props.disabled && !loading && variant === 'primary') ||
      variant === 'secondary' ||
      variant === 'secondary_black';

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          loading && 'pointer-events-none',
          'overflow-y-clip',
          //NOTE: we can't use group-hover here, because this can be used inside cards, and cards contain group class
          {
            '[&:hover_.first-child]:-translate-y-16 [&:hover_.second-child]:translate-y-0':
              isAnimationEnabled,
          }
        )}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        <>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center w-full h-full">
              {/* FIXME: Remove null checkers. Currently cva types add null|undefined to the variants.
              That wwill be removed in the next release. Ref: https://beta.cva.style/getting-started/whats-new
            */}
              <LoadingDots variant={variant!} />
            </div>
          )}
          <div
            className={cn(
              'duration-200 w-full h-full transition-opacity flex justify-center',
              {
                'opacity-100': !loading,
                'opacity-0': loading,

                'grid grid-cols-1': isAnimationEnabled,
              }
            )}
          >
            <div
              className={cn(
                'flex items-center justify-center gap-2 first-child duration-200 transition-all ease-out',
                {
                  'row-start-1 col-start-1 first-child translate-y-0 duration-200 transition-all ease-out':
                    isAnimationEnabled,
                }
              )}
            >
              {props.children}
            </div>

            {isAnimationEnabled && (
              <div
                className={cn(
                  'row-start-1 col-start-1 flex items-center justify-center translate-y-16 gap-2 second-child duration-200 transition-all ease-out'
                )}
              >
                {props.children}
              </div>
            )}
          </div>
        </>
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
